/**
 * The one request contract (A31, F-4.5).
 *
 * The guided form, DANI's typed entry, one-question mode and (later) the client
 * self-serve form all produce exactly this object, and the engine accepts
 * nothing else. There is no per-entry-path logic anywhere downstream: two entry
 * paths that describe the same study produce byte-identical requests, so they
 * cannot produce two different numbers.
 *
 * Pure: no DB, no framework, no clock.
 */
import { z } from 'zod';

import { VISIT_CATEGORIES } from '../contracts/pql';
import { LockoutSchema } from './lockout';
import { StatedSchema } from './stated';
import { PROFILE_FIELD_KEYS } from './profile-fields';
import { MAX_ZIP_MARKETS } from '../ooh/residents';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

/** A bounded window: either "the last N days" or an explicit from/to. */
export const BehaviorWindowSchema = z
    .object({
        lastDays: z.number().int().min(1).max(730).optional(),
        from: isoDate.optional(),
        to: isoDate.optional(),
    })
    .refine((w) => w.lastDays != null || (w.from != null && w.to != null), {
        message: 'Give a number of days, or both a from and a to date',
    })
    .refine((w) => w.from == null || w.to == null || w.from <= w.to, {
        message: 'The window starts after it ends',
    });

export type BehaviorWindow = z.infer<typeof BehaviorWindowSchema>;

export const GENDERS = ['male', 'female', 'non_binary'] as const;
export type Gender = (typeof GENDERS)[number];

/**
 * The genders a person can pick. The panel records only male and female (the
 * behavioural data's PANELIST_DEMOGRAPHIC and the standard panel's gender
 * options, checked 2026-10-06), so non-binary stays in the contract for checks
 * already stored but is not offered: a check on it was refused or widened.
 * Add it here when the panel records it.
 */
export const SELECTABLE_GENDERS = ['female', 'male'] as const satisfies readonly Gender[];

export const GENDER_LABEL: Record<Gender, string> = {
    female: 'Female',
    male: 'Male',
    non_binary: 'Non-binary',
};

/** What a gender picker offers: the selectable genders, plus any a reopened check already holds. */
export function genderChoices(selected: readonly string[] = []): { key: Gender; label: string }[] {
    const selectable: readonly string[] = SELECTABLE_GENDERS;
    const keys = new Set<string>([...SELECTABLE_GENDERS, ...selected]);
    return [...SELECTABLE_GENDERS, ...GENDERS.filter((g) => !selectable.includes(g))]
        .filter((g) => keys.has(g))
        .map((key) => ({ key, label: GENDER_LABEL[key] }));
}

export const DemographicCriterionSchema = z.object({
    kind: z.literal('demographic'),
    age: z
        .object({ min: z.number().int().min(13).max(99), max: z.number().int().min(13).max(99) })
        .refine((a) => a.min <= a.max, { message: 'Minimum age is above maximum age' })
        .optional(),
    genders: z.array(z.enum(GENDERS)).optional(),
    /** Two-letter state codes. */
    states: z.array(z.string().length(2)).optional(),
    /** DMA names as the panel records them. */
    dmas: z.array(z.string().min(1)).optional(),
    /** Census regions: Northeast, Midwest, South, West. */
    regions: z.array(z.string().min(1)).optional(),
    /**
     * Other panel profile fields (profile-fields.ts): field → the answers
     * wanted, as the panel labels them. Counted on the standard panel only.
     */
    /** Zip codes, and how far around them counts (0 means inside the zip). Standard panel only. */
    zips: z
        .object({
            codes: z
                .array(z.string().regex(/^\d{5}$/, 'Zip codes are five digits.'))
                .min(1)
                .max(5000),
            radiusMiles: z.number().min(0).max(100),
        })
        .optional(),
    /** A list of panelist IDs to count only, or to leave out. Standard panel only. */
    panelists: z
        .object({
            name: z.string().trim().min(1).max(120),
            ids: z
                .array(z.string().regex(/^\d+$/, 'Panelist IDs are numbers.'))
                .min(1)
                .max(100_000),
            exclude: z.boolean(),
        })
        .optional(),
    profile: z
        .record(z.string(), z.array(z.string().min(1)).min(1).max(400))
        .refine((p) => Object.keys(p).every((k) => (PROFILE_FIELD_KEYS as string[]).includes(k)), {
            message: 'Unknown profile field.',
        })
        .optional(),
});

/** People who used a named app within a bounded window (A12, Amendment 2). */
export const AppUsageCriterionSchema = z.object({
    kind: z.literal('app_usage'),
    apps: z.array(z.string().min(1)).min(1),
    window: BehaviorWindowSchema,
});

/** People who visited a named domain within a bounded window (A12, Amendment 2). */
export const WebVisitCriterionSchema = z.object({
    kind: z.literal('web_visit'),
    domains: z.array(z.string().min(1)).min(1),
    window: BehaviorWindowSchema,
});

/**
 * People with a receipt from a named store within a bounded window (A12), from
 * the receipts panelists share (store and dollar amount, not item level).
 * `minSpend` keeps only people whose receipts from those stores add up to at
 * least that many dollars in the window.
 */
export const PurchaseCriterionSchema = z.object({
    kind: z.literal('purchase'),
    stores: z.array(z.string().min(1).max(160)).min(1).max(20),
    window: BehaviorWindowSchema,
    minSpend: z.number().positive().max(100_000).optional(),
});

/**
 * Panelists tied to named store chains (A10): GPS-verified visitors for in-store
 * and exit-interview studies, panelists living near a location for directed shop.
 * Every location of each chain counts, unless a saved store list with each
 * location names the stores (UAT UI-008).
 */
export const VenueCriterionSchema = z.object({
    kind: z.literal('venue'),
    chains: z
        .array(
            z.object({
                chainId: z.string().min(1),
                name: z.string().min(1).max(160),
                /** Completes wanted at this store (UAT MT-005): each store is its own group. */
                target: z.number().int().min(1).max(1_000_000).optional(),
            }),
        )
        .min(1)
        .max(20),
    /** In-store and exit interview: the shortest visit that counts. Default 5 minutes. */
    dwellMinutes: z.number().int().min(1).max(240).optional(),
    /** Directed shop: how far from a location a panelist may be. Default 5 miles. */
    radiusMiles: z.number().int().min(1).max(25).optional(),
    /** A saved store list with each location (UAT UI-008): only its stores are counted. */
    listId: z.string().min(1).optional(),
    /** Shown in the read-back; the list itself is read by id. */
    listName: z.string().min(1).max(160).optional(),
});

/**
 * People who visited stores in a past window: chains, categories of place, a
 * saved stores list, or any mix (Visit history's audience, handed to a check).
 * Counted from the store-visit data the way Visit history counts it, so a
 * survey of past visitors starts from the same number. Unlike `venue` it needs
 * no chain to be in the panel's store list, and it takes categories and
 * independent stores; it is for surveying people who went there, not for
 * intercepting them in a store.
 */
export const StoreVisitCriterionSchema = z.object({
    kind: z.literal('store_visit'),
    chains: z
        .array(z.object({ chainId: z.string().min(1), name: z.string().min(1).max(160) }))
        .max(20)
        .default([]),
    /** "any": visited at least one chain; "all": visited every one of them (chains only). */
    chainMatch: z.enum(['any', 'all']).default('any'),
    categories: z.array(z.enum(VISIT_CATEGORIES)).max(10).default([]),
    /** A saved stores list (lists-store), read when the check runs. */
    listId: z.string().min(1).optional(),
    /** Shown in the read-back; the list itself is read by id. */
    listName: z.string().min(1).max(160).optional(),
    window: BehaviorWindowSchema,
    dwellMinutes: z.number().int().min(1).max(240).default(5),
    confidence: z.enum(['any', 'medium', 'high']).default('medium'),
});

/** A store_visit criterion's own rules (checked on the criteria list: a union member cannot carry refinements). */
/** An out-of-home criterion's own rules (a union member cannot carry refinements). */
export function oohIssues(c: z.infer<typeof OohExposureCriterionSchema>): string[] {
    const out: string[] = [];
    const airports = c.airports ?? [];
    const zips = c.residents?.zips ?? [];
    if (!c.placements.length && !airports.length && !zips.length)
        out.push('Add the test locations, the airports the campaign runs in, or its zip codes');
    if (zips.length && (c.placements.length || airports.length))
        out.push('Count by zip codes or by locations, not both: run them as separate checks');
    if (zips.length && c.control) out.push('A control list works with locations, not zip codes');
    const markets = c.residents?.markets ?? [];
    if (markets.length) {
        const listed = new Set(zips);
        const named = markets.flatMap((m) => m.zips);
        if (named.some((z) => !listed.has(z)))
            out.push('Every zip code in a market must be in the zip list');
        if (new Set(named).size !== named.length) out.push('A zip code can be in one market only');
        if (new Set(markets.map((m) => m.name.toLowerCase())).size !== markets.length)
            out.push('Each market can be named once');
    }
    if (c.placements.length && airports.length)
        out.push(
            'Count by locations or by airports, not both: run the airports as their own check',
        );
    if (airports.length && c.control) out.push('A control list works with locations, not airports');
    if (new Set(airports.map((a) => a.code)).size !== airports.length)
        out.push('Each airport can be named once');
    return out;
}

export function storeVisitIssues(c: z.infer<typeof StoreVisitCriterionSchema>): string[] {
    const out: string[] = [];
    if (!c.chains.length && !c.categories.length && !c.listId)
        out.push('Pick a chain, a category or a saved list');
    if (c.chainMatch === 'all' && (c.chains.length < 2 || c.categories.length || c.listId))
        out.push('Visited every one works with two or more chains only');
    return out;
}

/**
 * Most locations in one list (test, or control). A list is one bound JSON value
 * in one Snowflake statement; GSTV's control lists run to about 3,000 stations.
 */
export const MAX_OOH_PLACEMENTS = 5000;

/** Out-of-home networks with their own measured rates; every other network is `other`. */
export const OOH_NETWORKS = ['vistar', 'gstv', 'amazon', 'other'] as const;
export type OohNetwork = (typeof OOH_NETWORKS)[number];

const PlacementSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    radiusMeters: z.number().int().min(10).max(1000),
    type: z.string().max(80).optional(),
});

/**
 * Panelists whose phone was near an out-of-home placement during a window: the
 * exposed group of an OOH measurement study. Each placement is a point and a
 * geofence radius; `type` is the media type from the upload, kept for the
 * read-back.
 *
 * `control` is the study's control group: panelists near the control locations
 * in the same window. Its `target` is the control group's share of the study's
 * completes; the test group gets the rest. Its `overlap` says where someone
 * near both a test and a control location counts (control unless the bid says).
 */
/**
 * An airport an out-of-home campaign runs in (UAT UI-020): its IATA code. Who
 * was exposed is who visited the airport in the visit data, so no ad frame
 * coordinates are needed. `target` is that airport's own completes.
 */
export const AirportSchema = z.object({
    code: z.string().regex(/^[A-Z]{3}$/, 'Use the airport’s three-letter code'),
    target: z.number().int().min(1).max(1_000_000).optional(),
});
export type Airport = z.infer<typeof AirportSchema>;

/** Most airports one check counts, each its own group. */
export const MAX_AIRPORTS = 12;

export const OohExposureCriterionSchema = z.object({
    kind: z.literal('ooh_exposure'),
    /** Empty when the campaign is counted by airport instead. */
    placements: z.array(PlacementSchema).max(MAX_OOH_PLACEMENTS),
    /** Airports, counted as their visitors; in place of placements, not beside them. */
    airports: z.array(AirportSchema).max(MAX_AIRPORTS).optional(),
    /**
     * Zip codes, counted as the panelists living within `radiusMiles` of each
     * (`nycRadiusMiles` in New York City), the Bid Desk's way of pricing a bid
     * sent as zips (ooh/residents.ts). In place of placements, not beside them.
     */
    residents: z
        .object({
            zips: z
                .array(z.string().regex(/^\d{5}$/, 'Use five-digit zip codes'))
                .min(1)
                .max(MAX_OOH_PLACEMENTS),
            radiusMiles: z.number().min(1).max(100),
            nycRadiusMiles: z.number().min(1).max(100),
            /** The list's markets, one row each; every zip in them is also in `zips`. */
            markets: z
                .array(
                    z.object({
                        name: z.string().trim().min(1).max(60),
                        zips: z.array(z.string().regex(/^\d{5}$/)).min(1),
                        target: z.number().int().min(1).max(1_000_000).optional(),
                    }),
                )
                .max(MAX_ZIP_MARKETS)
                .optional(),
            /** How many locations the zips were looked up from (a list with latitude and longitude). */
            fromLocations: z.number().int().min(1).optional(),
            /** The people living near the zips (default), or those seen near them in the window. */
            basis: z.enum(['home', 'seen']).optional(),
        })
        .optional(),
    window: BehaviorWindowSchema,
    /**
     * The media network running the campaign (accuracy review 2026-10-20): how
     * many of the people exposed are sent the survey differs by network and
     * campaign size. Unsaid, the all-network share applies.
     */
    network: z.enum(OOH_NETWORKS).optional(),
    control: z
        .object({
            placements: z.array(PlacementSchema).min(1).max(MAX_OOH_PLACEMENTS),
            target: z.number().int().min(1).max(1_000_000).optional(),
            /**
             * Where a panelist seen at both a test and a control location counts
             * (M4S-6002). Unsaid, the control group: the test group is kept clean
             * of anyone also seen at a control location, the rule the Bid Desk
             * applies to every study. `test` counts them as exposed; `neither`
             * leaves them out.
             */
            overlap: z.enum(['test', 'control', 'neither']).optional(),
        })
        .optional(),
});

export type OohOverlapRule = 'test' | 'control' | 'neither';

/** The rule a control list applies, `control` when unsaid. */
export const overlapRuleOf = (control: { overlap?: OohOverlapRule } | undefined): OohOverlapRule =>
    control?.overlap ?? 'control';

/** What each rule means, in the form's words. */
export const OOH_OVERLAP_LABEL: Record<OohOverlapRule, string> = {
    control: 'In the control group (a clean test group)',
    test: 'In the test group',
    neither: 'In neither group',
};

/** Study types whose criteria are an uploaded placement list. */
export function oohTargeting(studyTypeKey: string): boolean {
    return studyTypeKey === 'ooh_ad_measurement';
}

/**
 * Which store targeting a study type takes: visitors who stayed long enough
 * (`visit`), panelists living nearby (`nearby`), or none. The portal's B&M
 * methodologies, so it is fixed by what the source can count.
 */
export function storeTargeting(studyTypeKey: string): 'visit' | 'nearby' | null {
    if (
        studyTypeKey === 'in_store_intercept' ||
        studyTypeKey === 'exit_interview' ||
        studyTypeKey === EXIT_24H_STUDY_TYPE
    )
        return 'visit';
    if (studyTypeKey === 'directed_shop') return 'nearby';
    return null;
}

/**
 * People who switched device brand or carrier (A101), by PQL's rules: a switch
 * counts after a 14-day commitment, and a person stays a switcher for 90 days.
 * Not live yet (feature `switchers`).
 */
export const DeviceSwitchCriterionSchema = z.object({
    kind: z.literal('device_switch'),
    switchType: z.enum(['device_brand', 'carrier']),
    /** Switched away from any of these. Empty = from anything. */
    from: z.array(z.string().min(1).max(80)).max(20).optional(),
    /** Switched to any of these. */
    to: z.array(z.string().min(1).max(80)).min(1).max(20),
    window: BehaviorWindowSchema,
});

export const PARTICIPATION_OUTCOMES = ['completed', 'terminated', 'retired', 'waypoint'] as const;

/**
 * Include or exclude people by past survey participation (A102). Not live yet
 * (feature `participation`).
 */
export const SurveyParticipationCriterionSchema = z.object({
    kind: z.literal('survey_participation'),
    mode: z.enum(['include', 'exclude']),
    surveyIds: z.array(z.number().int().min(1)).min(1).max(50),
    outcomes: z.array(z.enum(PARTICIPATION_OUTCOMES)).min(1),
});

export const CriterionSchema = z.discriminatedUnion('kind', [
    DemographicCriterionSchema,
    AppUsageCriterionSchema,
    WebVisitCriterionSchema,
    PurchaseCriterionSchema,
    VenueCriterionSchema,
    OohExposureCriterionSchema,
    DeviceSwitchCriterionSchema,
    SurveyParticipationCriterionSchema,
    StoreVisitCriterionSchema,
]);

export type DemographicCriterion = z.infer<typeof DemographicCriterionSchema>;
export type AppUsageCriterion = z.infer<typeof AppUsageCriterionSchema>;
export type WebVisitCriterion = z.infer<typeof WebVisitCriterionSchema>;
export type PurchaseCriterion = z.infer<typeof PurchaseCriterionSchema>;
export type VenueCriterion = z.infer<typeof VenueCriterionSchema>;
export type OohExposureCriterion = z.infer<typeof OohExposureCriterionSchema>;
export type DeviceSwitchCriterion = z.infer<typeof DeviceSwitchCriterionSchema>;
export type SurveyParticipationCriterion = z.infer<typeof SurveyParticipationCriterionSchema>;
export type StoreVisitCriterion = z.infer<typeof StoreVisitCriterionSchema>;

/** Criteria kinds whose counts are not live yet, and the feature each belongs to. */
export const NOT_YET_COUNTED_KINDS = {} as const;

export const QUOTA_DIMENSIONS = ['gender', 'age', 'region', 'state', 'dma'] as const;
export type QuotaDimensionKind = (typeof QUOTA_DIMENSIONS)[number];

/**
 * One quota cell (Amendment 4, A81): a value set, or an age range. A cell
 * gives a number or a share of the study total, never both; neither = natural
 * fallout (A86).
 */
export const QuotaCellSchema = z
    .object({
        label: z.string().min(1).max(80),
        values: z.array(z.string().min(1)).max(60).optional(),
        age: z
            .object({
                min: z.number().int().min(13).max(99),
                max: z.number().int().min(13).max(99),
            })
            .refine((a) => a.min <= a.max, { message: 'Minimum age is above maximum age' })
            .optional(),
        target: z.number().int().min(1).max(1_000_000).optional(),
        share: z.number().gt(0).max(1).optional(),
    })
    .refine((c) => !(c.target != null && c.share != null), {
        message: 'Give a number or a share, not both',
    });

export const QuotaDimensionSchema = z.object({
    kind: z.enum(QUOTA_DIMENSIONS),
    cells: z.array(QuotaCellSchema).min(2).max(12),
});

/**
 * Quota cells (A81–A88). Interlocked: every combination is a cell (max 2
 * dimensions). Independent: each dimension adds up to the total on its own
 * (max 3). Not live yet (feature `quotas`).
 */
export const QuotasSchema = z
    .object({
        mode: z.enum(['independent', 'interlocked']),
        dimensions: z.array(QuotaDimensionSchema).min(1).max(3),
    })
    .refine((q) => q.mode === 'independent' || q.dimensions.length <= 2, {
        message: 'Interlock at most two dimensions',
    })
    .superRefine((q, ctx) => {
        for (const issue of quotaOverlaps(q)) ctx.addIssue({ code: 'custom', message: issue });
    });

export type QuotaCell = z.infer<typeof QuotaCellSchema>;
export type QuotaDimension = z.infer<typeof QuotaDimensionSchema>;
export type Quotas = z.infer<typeof QuotasSchema>;

/**
 * Overlap inside a dimension is refused, never double-counted (A83): two age
 * cells sharing an age, a value in two cells, or a state and a DMA dimension
 * in the same request. Pure; the form shows these inline.
 */
export function quotaOverlaps(q: { dimensions: QuotaDimension[] }): string[] {
    const out: string[] = [];
    const kinds = q.dimensions.map((d) => d.kind);
    if (new Set(kinds).size !== kinds.length) out.push('Each dimension can appear once.');
    if (kinds.includes('state') && kinds.includes('dma'))
        out.push('Use states or DMAs, not both: a DMA sits inside states.');
    for (const d of q.dimensions) {
        if (d.kind === 'age') {
            const ranges = d.cells
                .map((c) => c.age)
                .filter((a): a is { min: number; max: number } => a != null)
                .sort((a, b) => a.min - b.min);
            if (ranges.length !== d.cells.length) out.push('Every age cell needs an age range.');
            for (let i = 1; i < ranges.length; i++)
                if (ranges[i].min <= ranges[i - 1].max)
                    out.push(
                        `Age cells ${ranges[i - 1].min}–${ranges[i - 1].max} and ${ranges[i].min}–${ranges[i].max} share an age.`,
                    );
        } else {
            const seen = new Map<string, string>();
            for (const c of d.cells) {
                if (!c.values?.length) out.push(`Cell "${c.label}" needs at least one value.`);
                for (const v of c.values ?? []) {
                    const prior = seen.get(v);
                    if (prior && prior !== c.label)
                        out.push(`"${v}" is in both "${prior}" and "${c.label}".`);
                    seen.set(v, c.label);
                }
            }
        }
    }
    return out;
}

/** A past project used to calibrate the estimate (A56–A60). Not live yet (feature `pastProjects`). */
export const CalibrationSchema = z.object({
    projects: z
        .array(
            z.object({
                projectId: z.string().min(1).max(40),
                label: z.string().min(1).max(160),
                achievedIncidence: z.number().gt(0).max(1).optional(),
                /**
                 * A past survey the person named as the baseline (Fease tool sync,
                 * 2026-10-27): matching compares the check with it alone. Chosen
                 * with one tap on a comparison, never applied silently.
                 */
                surveyId: z.number().int().positive().optional(),
            }),
        )
        .min(1)
        .max(5),
});
export type Calibration = z.infer<typeof CalibrationSchema>;

/** The exit-within-24-hours intercept variant (A98, A13). Not live yet (feature `intercept24h`). */
export const EXIT_24H_STUDY_TYPE = 'exit_interview_24h';
export type Criterion = z.infer<typeof CriterionSchema>;
export type CriterionKind = Criterion['kind'];

/**
 * One screener question and which answers qualify (Amendment 10). The wording
 * is matched to the same question in earlier surveys, so it should be the
 * wording the survey will use. Single selection: an answer in `qualifying`
 * qualifies. Multiple selection: a respondent qualifies by picking every
 * `required` answer, at least one `qualifying` answer when any are listed, and
 * no `disqualifying` answer.
 */
export const ScreenerQuestionSchema = z
    .object({
        question: z.string().trim().min(3).max(500),
        type: z.enum(['single', 'multi']),
        qualifying: z.array(z.string().trim().min(1).max(300)).max(100),
        required: z.array(z.string().trim().min(1).max(300)).max(50).optional(),
        disqualifying: z.array(z.string().trim().min(1).max(300)).max(100).optional(),
    })
    .refine(
        (q) =>
            q.type === 'single'
                ? q.qualifying.length > 0
                : q.qualifying.length + (q.required?.length ?? 0) + (q.disqualifying?.length ?? 0) >
                  0,
        { message: 'Say which answers qualify.' },
    );
export type ScreenerQuestion = z.infer<typeof ScreenerQuestionSchema>;

/** Where the incidence the engine uses came from (Amendment 10). */
/**
 * `past_studies`: nobody gave the share, and the person chose what past studies
 * like this one qualified (the one-tap suggestion, 2026-10-23). It is never
 * filled in on its own: offline, a silent stand-in put the real result outside
 * the range far more often (stated 74% inside, past studies 60%).
 */
export const INCIDENCE_SOURCES = ['stated', 'predicted', 'past_studies'] as const;
export type IncidenceSource = (typeof INCIDENCE_SOURCES)[number];

export const PROGRAMMING_TYPES = ['native', 'custom', 'external'] as const;
export type ProgrammingType = (typeof PROGRAMMING_TYPES)[number];

/** `builder`: a native Builder draft (docs/pdlc/builder.md, B3). Recorded, never branched on. */
export const ENTRY_PATHS = ['form', 'dani', 'one_question', 'builder'] as const;
export type EntryPath = (typeof ENTRY_PATHS)[number];

/**
 * Who one cell is, when it is not the whole study's audience (Ipsos-style cells,
 * 2026-10-14): the study's own demographic criterion with these fields
 * replacing its own, for example parents 30–45 in the Northeast.
 */
export const CellWhoSchema = DemographicCriterionSchema.pick({
    age: true,
    genders: true,
    states: true,
    dmas: true,
    regions: true,
    profile: true,
});
export type CellWho = z.infer<typeof CellWhoSchema>;

export const IncidenceScenarioSchema = z.object({
    /** What the group is, for example "Brand buyers". Optional. */
    label: z.string().trim().max(60).optional(),
    incidenceRate: z.number().gt(0).max(1),
    /** Completes wanted from this group, when the groups share one audience. */
    target: z.number().int().min(1).max(1_000_000).optional(),
    /** The group's own audience, when it differs from the study's (counted on its own). */
    who: CellWhoSchema.optional(),
});

/**
 * Mutually exclusive cells: nobody is counted in, or recruited into, two of the
 * groups. Each group fills in turn and the people it uses are taken from the
 * groups after it. `boosts_first` fills the smallest groups first, as clients
 * ask ("prioritize boosts"); `listed` keeps the order given.
 */
export const ExclusiveCellsSchema = z.object({
    priority: z.enum(['boosts_first', 'listed']),
});
export type ExclusiveCells = z.infer<typeof ExclusiveCellsSchema>;
export type IncidenceScenario = z.infer<typeof IncidenceScenarioSchema>;

export const FeasibilityRequestSchema = z.object({
    version: z.literal(1),
    /** Human label shown in history. Never affects the math. */
    title: z.string().max(160).optional(),
    /** Who the study is for, so History can find every check for a client. Never affects the math. */
    client: z.string().max(120).optional(),
    /** Key into the study-type registry (pdlc_feasibility_study_type). */
    studyType: z.string().min(1),
    /** A tracker or longitudinal study: how each wave is fielded, a study type key (engine/waves.ts, UAT UI-018). */
    fieldedAs: z.string().min(1).max(60).optional(),
    /** Who is left out for having taken earlier surveys (engine/lockout.ts, UAT UI-017). Wave studies default on. */
    lockout: LockoutSchema.optional(),
    /** Exit checks: stated visitors added to the exit number (engine/stated.ts, UAT MT-004). On by default. */
    stated: StatedSchema.optional(),
    /** Completes wanted. Null = ceiling mode: "how many can we get?" (A7, F-2.6). */
    target: z.number().int().min(1).max(1_000_000).nullable(),
    /**
     * Incidence, 0–1: the share of starters who qualify. Stated by the person
     * (the client's estimate), or the engine's prediction from the screener
     * when `incidenceSource` is `predicted` (Amendment 10).
     */
    incidenceRate: z.number().gt(0).max(1),
    /** Absent means stated. `predicted` needs `screener`; `past_studies` is a chosen suggestion. */
    incidenceSource: z.enum(INCIDENCE_SOURCES).optional(),
    /** The screener questions the engine predicts incidence from. */
    screener: z.array(ScreenerQuestionSchema).min(1).max(15).optional(),
    /** Length of the fielding window in days. Drives accrual (A4, A7). */
    fieldDays: z.number().int().min(1).max(365),
    /** Optional start date, only for display and the accrual chart's axis. */
    fieldStart: isoDate.optional(),
    programmingType: z.enum(PROGRAMMING_TYPES),
    /** Photo or video capture required. Selects the stricter acceptance factor. */
    mediaCapture: z.boolean(),
    /**
     * Survey length in minutes, from the client's spec (A156). Panelists see it
     * before they start; the funnel applies the measured length adjustments.
     * Absent, the estimate uses the methodology's typical length.
     */
    loiMinutes: z.number().int().min(1).max(120).optional(),
    /**
     * Incentive per complete, in cents. Ours to set (A157): absent, the estimate
     * assumes the methodology's standard incentive and recommends one when that
     * falls short. Set by the incentive fix, or by a person on purpose. Internal only.
     */
    incentiveCents: z.number().int().min(0).max(100_000).optional(),
    /**
     * How many people the team will invite (UAT backtest, 2026-10-15). On the
     * offer basis (draft Amendment 13) it is the release size: how many get the
     * survey in the app's survey box, not how many are pushed, spread the way
     * past releases went (offer_mix_share), with the usual pushes on top. On the
     * push basis the sends are spread the way past pushes were (push_mix_share)
     * and the app's survey list adds what it brings (survey_list_complete_ratio).
     * Absent, the estimate is the audience's capacity. Internal only.
     */
    plannedSends: z.number().int().min(1).max(10_000_000).optional(),
    /**
     * In-store and exit: how many people the survey is planned to be sent to at
     * the stores (its send plan or cap), when the program has one. Stated, it
     * replaces the sends estimated from store visits: a client's sends per
     * visitor can swing tenfold between programs (NACS, 2023-2026), so its plan
     * says more than its history. Internal only.
     */
    plannedStoreSends: z.number().int().min(1).max(10_000_000).optional(),
    criteria: z
        .array(CriterionSchema)
        .max(12)
        .superRefine((list, ctx) => {
            for (const c of list) {
                if (c.kind === 'store_visit')
                    for (const message of storeVisitIssues(c))
                        ctx.addIssue({ code: 'custom', message });
                if (c.kind === 'ooh_exposure')
                    for (const message of oohIssues(c)) ctx.addIssue({ code: 'custom', message });
            }
        }),
    /** Quota cells (A81). The audience is the union of the cells; `target` stays the study total. */
    quotas: QuotasSchema.optional(),
    /** Past projects that calibrate the estimate (A56–A60). */
    calibration: CalibrationSchema.optional(),
    /**
     * Also answer at these incidence rates: the same audience, rerun at each
     * rate. A rate with its own completes is a group sharing the audience with
     * the others, and the result says what share of the audience they need
     * together, so nobody is counted twice.
     */
    incidenceScenarios: z.array(IncidenceScenarioSchema).min(1).max(8).optional(),
    /** The groups above cannot share panelists (Ipsos-style mutually exclusive cells). */
    exclusiveCells: ExclusiveCellsSchema.optional(),
    /** Multi-phase studies: how many phases this study has, when not the study type's usual number. */
    phases: z.number().int().min(2).max(12).optional(),
    /** Multi-phase studies: days panelists wait between phases (an in-home test's usage period). */
    // 0: the phases run back to back (a directed shop with no wait, UAT UI-037).
    daysBetweenPhases: z.number().int().min(0).max(120).optional(),
    /**
     * Directed shop: whether each mission asks the panelist to buy something.
     * Recorded with the check so past and future studies can be compared on it;
     * the history does not hold it yet, so it does not move the estimate.
     */
    missionPurchase: z.boolean().optional(),
    /** In-home use tests: products each panelist tests. */
    // 0: no product is tested (findability or shelf research, UAT UI-038).
    productsTested: z.number().int().min(0).max(10).optional(),
    /** Which entry path produced this request. Recorded, never branched on (A31). */
    entry: z.enum(ENTRY_PATHS),
});

export type FeasibilityRequest = z.infer<typeof FeasibilityRequestSchema>;

/** Profile fields with sorted keys and sorted, unique answers; empty fields dropped. */
function canonicalProfile(
    p: Record<string, string[]> | undefined,
): Record<string, string[]> | undefined {
    if (!p) return undefined;
    const entries = Object.keys(p)
        .sort()
        .map((k) => [k, [...new Set(p[k].map((v) => v.trim()).filter(Boolean))].sort()] as const)
        .filter(([, v]) => v.length);
    return entries.length ? Object.fromEntries(entries) : undefined;
}

/** ~11 cm at 6 decimals: the same placement typed twice is one placement. Sorted. */
function canonicalPlacements(
    placements: OohExposureCriterion['placements'],
): OohExposureCriterion['placements'] {
    const round = (n: number) => Math.round(n * 1e6) / 1e6;
    const byKey = new Map<string, OohExposureCriterion['placements'][number]>();
    for (const p of placements) {
        const next = {
            lat: round(p.lat),
            lng: round(p.lng),
            radiusMeters: p.radiusMeters,
            ...(p.type?.trim() ? { type: p.type.trim() } : {}),
        };
        byKey.set(`${next.lat},${next.lng},${next.radiusMeters}`, next);
    }
    return [...byKey.values()].sort(
        (a, b) => a.lat - b.lat || a.lng - b.lng || a.radiusMeters - b.radiusMeters,
    );
}

/**
 * Canonical form: the same study always serialises the same way, whichever
 * path produced it. Sorted arrays, no empty criteria, no undefined keys. The
 * conformance test (F-4.5) compares these, and saved checks store this form.
 */
export function canonicalizeRequest(input: FeasibilityRequest): FeasibilityRequest {
    const sortStrings = (xs: readonly string[] | undefined) =>
        xs && xs.length ? [...new Set(xs.map((x) => x.trim()))].sort() : undefined;

    const criteria: Criterion[] = [];
    for (const c of input.criteria) {
        if (c.kind === 'demographic') {
            const next: DemographicCriterion = {
                kind: 'demographic',
                ...(c.age ? { age: { min: c.age.min, max: c.age.max } } : {}),
                ...(sortStrings(c.genders)
                    ? { genders: sortStrings(c.genders) as DemographicCriterion['genders'] }
                    : {}),
                ...(sortStrings(c.states?.map((s) => s.toUpperCase()))
                    ? { states: sortStrings(c.states?.map((s) => s.toUpperCase())) }
                    : {}),
                ...(sortStrings(c.dmas) ? { dmas: sortStrings(c.dmas) } : {}),
                ...(sortStrings(c.regions) ? { regions: sortStrings(c.regions) } : {}),
                ...(c.zips?.codes.length
                    ? {
                          zips: {
                              codes: [...new Set(c.zips.codes)].sort(),
                              radiusMiles: c.zips.radiusMiles,
                          },
                      }
                    : {}),
                ...(c.panelists?.ids.length
                    ? {
                          panelists: {
                              name: c.panelists.name.trim(),
                              ids: [...new Set(c.panelists.ids)].sort(),
                              exclude: c.panelists.exclude,
                          },
                      }
                    : {}),
                ...(canonicalProfile(c.profile) ? { profile: canonicalProfile(c.profile) } : {}),
            };
            if (Object.keys(next).length > 1) criteria.push(next);
        } else if (c.kind === 'app_usage') {
            criteria.push({
                kind: 'app_usage',
                apps: sortStrings(c.apps) ?? [],
                window: canonicalWindow(c.window),
            });
        } else if (c.kind === 'web_visit') {
            criteria.push({
                kind: 'web_visit',
                domains: sortStrings(c.domains.map(normalizeDomain)) ?? [],
                window: canonicalWindow(c.window),
            });
        } else if (c.kind === 'purchase') {
            criteria.push({
                kind: 'purchase',
                stores: sortStrings(c.stores) ?? [],
                window: canonicalWindow(c.window),
                ...(c.minSpend ? { minSpend: Math.round(c.minSpend * 100) / 100 } : {}),
            });
        } else if (c.kind === 'ooh_exposure') {
            criteria.push({
                kind: 'ooh_exposure',
                placements: canonicalPlacements(c.placements),
                ...(c.airports?.length
                    ? {
                          airports: [...c.airports]
                              .sort((a, b) => a.code.localeCompare(b.code))
                              .map((a) => ({
                                  code: a.code,
                                  ...(a.target ? { target: a.target } : {}),
                              })),
                      }
                    : {}),
                ...(c.residents
                    ? {
                          residents: {
                              zips: [...new Set(c.residents.zips)].sort(),
                              radiusMiles: c.residents.radiusMiles,
                              nycRadiusMiles: c.residents.nycRadiusMiles,
                              ...(c.residents.markets?.length
                                  ? {
                                        markets: [...c.residents.markets]
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((m) => ({
                                                name: m.name,
                                                zips: [...new Set(m.zips)].sort(),
                                                ...(m.target ? { target: m.target } : {}),
                                            })),
                                    }
                                  : {}),
                              ...(c.residents.fromLocations
                                  ? { fromLocations: c.residents.fromLocations }
                                  : {}),
                              ...(c.residents.basis === 'seen' ? { basis: 'seen' as const } : {}),
                          },
                      }
                    : {}),
                window: canonicalWindow(c.window),
                ...(c.network ? { network: c.network } : {}),
                ...(c.control
                    ? {
                          control: {
                              placements: canonicalPlacements(c.control.placements),
                              ...(c.control.target ? { target: c.control.target } : {}),
                              // Always written, so a stored check keeps the rule it ran
                              // under if the default ever moves again.
                              overlap: overlapRuleOf(c.control),
                          },
                      }
                    : {}),
            });
        } else if (c.kind === 'device_switch') {
            criteria.push({
                kind: 'device_switch',
                switchType: c.switchType,
                ...(sortStrings(c.from) ? { from: sortStrings(c.from) } : {}),
                to: sortStrings(c.to) ?? [],
                window: canonicalWindow(c.window),
            });
        } else if (c.kind === 'survey_participation') {
            criteria.push({
                kind: 'survey_participation',
                mode: c.mode,
                surveyIds: [...new Set(c.surveyIds)].sort((a, b) => a - b),
                outcomes: [...new Set(c.outcomes)].sort(),
            });
        } else if (c.kind === 'store_visit') {
            const byId = new Map(c.chains.map((ch) => [ch.chainId.trim(), ch.name.trim()]));
            criteria.push({
                kind: 'store_visit',
                chains: [...byId.entries()]
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([chainId, name]) => ({ chainId, name })),
                chainMatch: c.chainMatch,
                categories: [...new Set(c.categories)].sort(),
                ...(c.listId ? { listId: c.listId } : {}),
                ...(c.listId && c.listName ? { listName: c.listName.trim() } : {}),
                window: canonicalWindow(c.window),
                dwellMinutes: c.dwellMinutes,
                confidence: c.confidence,
            });
        } else {
            const byId = new Map(
                c.chains.map((ch) => [
                    ch.chainId.trim(),
                    { name: ch.name.trim(), target: ch.target },
                ]),
            );
            criteria.push({
                kind: 'venue',
                chains: [...byId.entries()]
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([chainId, { name, target }]) => ({
                        chainId,
                        name,
                        ...(target ? { target } : {}),
                    })),
                ...(c.dwellMinutes ? { dwellMinutes: c.dwellMinutes } : {}),
                ...(c.radiusMiles ? { radiusMiles: c.radiusMiles } : {}),
                // A store list with each location counts only its stores (UAT UI-008).
                ...(c.listId ? { listId: c.listId } : {}),
                ...(c.listId && c.listName ? { listName: c.listName.trim() } : {}),
            });
        }
    }
    criteria.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

    return {
        version: 1,
        ...(input.title?.trim() ? { title: input.title.trim() } : {}),
        ...(input.client?.trim() ? { client: input.client.trim() } : {}),
        studyType: input.studyType,
        ...(input.fieldedAs ? { fieldedAs: input.fieldedAs } : {}),
        ...(input.lockout
            ? {
                  lockout: {
                      mode: input.lockout.mode,
                      ...(input.lockout.mode !== 'off' && input.lockout.surveyIds?.length
                          ? {
                                surveyIds: [...new Set(input.lockout.surveyIds)].sort(
                                    (a, b) => a - b,
                                ),
                            }
                          : {}),
                      ...(input.lockout.mode !== 'off' && input.lockout.lastDays
                          ? { lastDays: input.lockout.lastDays }
                          : {}),
                  },
              }
            : {}),
        ...(input.stated
            ? {
                  stated: input.stated.off
                      ? { off: true }
                      : {
                            ...(input.stated.share
                                ? { share: Math.round(input.stated.share * 10_000) / 10_000 }
                                : {}),
                            ...(input.stated.target ? { target: input.stated.target } : {}),
                        },
              }
            : {}),
        target: input.target,
        incidenceRate: Math.round(input.incidenceRate * 10_000) / 10_000,
        ...(input.incidenceSource === 'predicted' || input.incidenceSource === 'past_studies'
            ? { incidenceSource: input.incidenceSource }
            : {}),
        ...(input.screener?.length
            ? { screener: input.screener.map(canonicalScreenerQuestion) }
            : {}),
        fieldDays: input.fieldDays,
        ...(input.fieldStart ? { fieldStart: input.fieldStart } : {}),
        // A custom build by MFour runs in Studio: SOTG cannot tell them apart,
        // so they share one finish rate and one answer.
        programmingType: input.programmingType === 'custom' ? 'native' : input.programmingType,
        mediaCapture: input.mediaCapture,
        ...(input.loiMinutes ? { loiMinutes: input.loiMinutes } : {}),
        ...(input.incentiveCents != null ? { incentiveCents: input.incentiveCents } : {}),
        ...(input.plannedSends ? { plannedSends: input.plannedSends } : {}),
        ...(input.plannedStoreSends ? { plannedStoreSends: input.plannedStoreSends } : {}),
        criteria,
        ...(input.quotas ? { quotas: canonicalQuotas(input.quotas, input.target) } : {}),
        ...(input.calibration
            ? {
                  calibration: {
                      projects: [...input.calibration.projects].sort((a, b) =>
                          a.projectId.localeCompare(b.projectId),
                      ),
                  },
              }
            : {}),
        ...(input.phases ? { phases: input.phases } : {}),
        ...(input.daysBetweenPhases != null ? { daysBetweenPhases: input.daysBetweenPhases } : {}),
        ...(input.productsTested != null ? { productsTested: input.productsTested } : {}),
        ...(input.missionPurchase != null ? { missionPurchase: input.missionPurchase } : {}),
        ...(input.incidenceScenarios?.length
            ? {
                  incidenceScenarios: input.incidenceScenarios.map((s) => ({
                      ...(s.label?.trim() ? { label: s.label.trim() } : {}),
                      incidenceRate: Math.round(s.incidenceRate * 10_000) / 10_000,
                      ...(s.target ? { target: s.target } : {}),
                      ...(s.who && Object.keys(canonicalWho(s.who)).length
                          ? { who: canonicalWho(s.who) }
                          : {}),
                  })),
              }
            : {}),
        ...(input.exclusiveCells && (input.incidenceScenarios?.length ?? 0) >= 2
            ? { exclusiveCells: { priority: input.exclusiveCells.priority } }
            : {}),
        entry: input.entry,
    };
}

/**
 * An age group's name is its range ("18–34", "55+"), so a group can never be
 * named one band and counted as another (UAT MT-009).
 */
export function ageBandLabel(age: { min: number; max: number }): string {
    return age.max >= 99 ? `${age.min}+` : `${age.min}–${age.max}`;
}

/**
 * Shares resolve to numbers against the study total, so the stored request and
 * `studyIdentity` are the same whether a cell was typed as 200 or as 50% of 400.
 * An age group is named by its range.
 */
function canonicalQuotas(q: Quotas, total: number | null): Quotas {
    return {
        mode: q.mode,
        dimensions: q.dimensions.map((d) => ({
            kind: d.kind,
            cells: d.cells.map((c) => {
                const target =
                    c.target ??
                    (c.share != null && total ? Math.round(c.share * total) : undefined);
                return {
                    label: c.age ? ageBandLabel(c.age) : c.label.trim(),
                    ...(c.values?.length ? { values: [...new Set(c.values)].sort() } : {}),
                    ...(c.age ? { age: { min: c.age.min, max: c.age.max } } : {}),
                    ...(target != null ? { target } : c.share != null ? { share: c.share } : {}),
                };
            }),
        })),
    };
}

/** The not-yet-live features a request uses. The real engine refuses these; stubs answer them. */
export function requestFeatures(
    request: FeasibilityRequest,
): ('quotas' | 'switchers' | 'participation' | 'intercept24h' | 'pastProjects')[] {
    const out = new Set<
        'quotas' | 'switchers' | 'participation' | 'intercept24h' | 'pastProjects'
    >();
    if (request.quotas) out.add('quotas');
    if (request.calibration) out.add('pastProjects');
    for (const c of request.criteria) {
        if (c.kind in NOT_YET_COUNTED_KINDS)
            out.add(NOT_YET_COUNTED_KINDS[c.kind as keyof typeof NOT_YET_COUNTED_KINDS]);
    }
    return [...out];
}

function canonicalWindow(w: BehaviorWindow): BehaviorWindow {
    return w.lastDays != null ? { lastDays: w.lastDays } : { from: w.from, to: w.to };
}

/** `https://www.att.com/wireless` → `att.com`. Domains compare on the registrable host. */
export function normalizeDomain(raw: string): string {
    const host = raw
        .trim()
        .toLowerCase()
        .replace(/^[a-z]+:\/\//, '')
        .split('/')[0]
        .replace(/^www\./, '');
    return host;
}

/** Answers sorted and de-duplicated, empty lists dropped: one screener, one canonical form. */
function canonicalScreenerQuestion(q: ScreenerQuestion): ScreenerQuestion {
    const list = (xs?: string[]) =>
        [...new Set((xs ?? []).map((x) => x.trim()).filter(Boolean))].sort();
    const required = list(q.required);
    const disqualifying = list(q.disqualifying);
    return {
        question: q.question.trim(),
        type: q.type,
        qualifying: list(q.qualifying),
        ...(q.type === 'multi' && required.length ? { required } : {}),
        ...(q.type === 'multi' && disqualifying.length ? { disqualifying } : {}),
    };
}

/**
 * Two 53-bit string hashes with different seeds (cyrb53), as 28 hex digits. Pure
 * and synchronous, so the identity is the same in the browser and on the server.
 */
function digest(text: string): string {
    const one = (seed: number) => {
        let h1 = 0xdeadbeef ^ seed;
        let h2 = 0x41c6ce57 ^ seed;
        for (let i = 0; i < text.length; i++) {
            const ch = text.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
        return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
    };
    return one(1) + one(2);
}

/**
 * A location list in the identity: its size and a digest, never the points. A
 * GSTV list of 4,000 stations is ~200 KB of JSON, and the identity is indexed,
 * where a value over 8 KB is refused and the check never starts.
 */
function compactLists(criteria: Criterion[]): unknown[] {
    const list = (p: OohExposureCriterion['placements']) => ({
        locations: p.length,
        digest: digest(JSON.stringify(p)),
    });
    return criteria.map((c) =>
        c.kind === 'ooh_exposure'
            ? {
                  ...c,
                  placements: list(c.placements),
                  ...(c.control
                      ? { control: { ...c.control, placements: list(c.control.placements) } }
                      : {}),
              }
            : c,
    );
}

/**
 * The request with these quotas, and the audience's age set to span the age
 * bands when there are any (UAT UI-027: a range was typed once in Who and again
 * on every band). The bands decide who is counted, so the audience follows them.
 */
export function withQuotas(
    request: FeasibilityRequest,
    quotas: Quotas | undefined,
): FeasibilityRequest {
    const bands = quotas?.dimensions
        .find((d) => d.kind === 'age')
        ?.cells.map((c) => c.age)
        .filter((a): a is { min: number; max: number } => a != null);
    if (!bands?.length) return { ...request, quotas };
    const min = Math.min(...bands.map((a) => a.min));
    const max = Math.max(...bands.map((a) => a.max));
    const has = request.criteria.some((c) => c.kind === 'demographic');
    const criteria: Criterion[] = has
        ? request.criteria.map((c) => (c.kind === 'demographic' ? { ...c, age: { min, max } } : c))
        : [{ kind: 'demographic', age: { min, max } }, ...request.criteria];
    return { ...request, quotas, criteria };
}

/**
 * A quota group type left with one group is not a split any more (UAT UI-034,
 * BT-2114: removing Men from Women/Men). The group type goes, and the audience
 * narrows to the group that is left: women only, ages 20–45, these states.
 */
export function narrowToCell(
    request: FeasibilityRequest,
    dimIndex: number,
    cell: QuotaCell,
): FeasibilityRequest {
    const quotas = request.quotas;
    const dim = quotas?.dimensions[dimIndex];
    if (!quotas || !dim) return request;
    const values = cell.values ?? [];
    const patch: Partial<DemographicCriterion> =
        dim.kind === 'gender'
            ? { genders: values as DemographicCriterion['genders'] }
            : dim.kind === 'age'
              ? cell.age
                  ? { age: cell.age }
                  : {}
              : dim.kind === 'state'
                ? { states: values }
                : dim.kind === 'dma'
                  ? { dmas: values }
                  : { regions: values };
    const has = request.criteria.some((c) => c.kind === 'demographic');
    const criteria: Criterion[] = has
        ? request.criteria.map((c) => (c.kind === 'demographic' ? { ...c, ...patch } : c))
        : [{ kind: 'demographic', ...patch }, ...request.criteria];
    const dimensions = quotas.dimensions.filter((_, i) => i !== dimIndex);
    return {
        ...request,
        criteria,
        quotas: dimensions.length
            ? { mode: dimensions.length === 1 ? 'independent' : quotas.mode, dimensions }
            : undefined,
    };
}

/** A list longer than this is kept in the identity as its length and a digest. */
const IDENTITY_LIST_MAX = 20;

/**
 * Every other long list (panelist IDs, zip codes, store locations, survey IDs)
 * as its length and a digest (UAT UI-024, BT-2106: a 2,607-ID list made the
 * identity too large for its index and the check never started).
 */
function compactLongLists(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.length > IDENTITY_LIST_MAX
            ? { items: value.length, digest: digest(JSON.stringify(value)) }
            : value.map(compactLongLists);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, compactLongLists(v)]));
    }
    return value;
}

/**
 * Postgres refuses a btree entry over about 2.7 KB, so an identity longer than
 * this is stored as a digest of itself: still equal for the same study.
 */
const IDENTITY_TEXT_MAX = 2000;

/** The request with its entry path removed — what "the same study" means (A31). */
export function studyIdentity(request: FeasibilityRequest): string {
    const canonical = canonicalizeRequest(request);
    // JSON.stringify drops undefined keys, so the entry path and title never count.
    const text = JSON.stringify({
        ...canonical,
        criteria: compactLongLists(
            canonical.criteria.some((c) => c.kind === 'ooh_exposure')
                ? compactLists(canonical.criteria)
                : canonical.criteria,
        ),
        entry: undefined,
        title: undefined,
        client: undefined,
    });
    return text.length > IDENTITY_TEXT_MAX ? `digest:${digest(text)}` : text;
}

/** The fields a person can change between DANI's read-back and the run, in form order. */
export const REQUEST_FIELDS = [
    'title',
    'client',
    'studyType',
    'fieldedAs',
    'lockout',
    'stated',
    'target',
    'incidenceRate',
    'incidenceSource',
    'screener',
    'fieldDays',
    'fieldStart',
    'programmingType',
    'mediaCapture',
    'loiMinutes',
    'incentiveCents',
    'plannedSends',
    'plannedStoreSends',
    'criteria',
    'quotas',
    'calibration',
    'incidenceScenarios',
    'exclusiveCells',
    'phases',
    'daysBetweenPhases',
    'productsTested',
    'missionPurchase',
] as const;

/** Which of those fields differ between two requests, compared in canonical form (F-4.3). */
export function changedRequestFields(
    before: FeasibilityRequest,
    after: FeasibilityRequest,
): (typeof REQUEST_FIELDS)[number][] {
    const a = canonicalizeRequest(before);
    const b = canonicalizeRequest(after);
    return REQUEST_FIELDS.filter(
        (k) => JSON.stringify(a[k] ?? null) !== JSON.stringify(b[k] ?? null),
    );
}

/** Resolves a behaviour window to explicit dates, given "today". */
export function resolveWindow(w: BehaviorWindow, today: string): { from: string; to: string } {
    if (w.from && w.to) return { from: w.from, to: w.to };
    const end = new Date(`${today}T00:00:00Z`);
    const start = new Date(end.getTime() - ((w.lastDays ?? 30) - 1) * 86_400_000);
    return { from: start.toISOString().slice(0, 10), to: today };
}

/** A cell's own audience in a canonical order: empty fields dropped, lists sorted. */
export function canonicalWho(w: CellWho): CellWho {
    const sorted = (xs: string[] | undefined) => (xs?.length ? [...new Set(xs)].sort() : undefined);
    const out: CellWho = {};
    if (w.age) out.age = { min: w.age.min, max: w.age.max };
    const genders = sorted(w.genders) as CellWho['genders'];
    if (genders) out.genders = genders;
    const states = sorted(w.states);
    if (states) out.states = states;
    const dmas = sorted(w.dmas);
    if (dmas) out.dmas = dmas;
    const regions = sorted(w.regions);
    if (regions) out.regions = regions;
    const profile = Object.fromEntries(
        Object.entries(w.profile ?? {})
            .filter(([, v]) => v.length)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => [k, [...new Set(v)].sort()]),
    );
    if (Object.keys(profile).length) out.profile = profile;
    return out;
}
